// SCR-CONSOLE-020 — یکپارچگی‌ها (US-INT-001، 031، 040، 042؛ AP-INT-02): کاتالوگ Adapter (نوع، سطح اعتبارنامه، وضعیت، نشان «نمونه»)،
// سلامت تجمیعی همه مراکز به تفکیک Adapter، اتصال‌های سطح پلتفرم (شاهکار، خط مشترک پیامک) با آزمون، فعال‌سازی و اعتبارنامه، و IPهای
// ثابت خروجی برای فهرست سفید سامانه‌های حاکمیتی.
import AddIcon from '@mui/icons-material/Add';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import NetworkCheckOutlinedIcon from '@mui/icons-material/NetworkCheckOutlined';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type AdapterHealthView,
  type AdapterView,
  type ConnectionTestView,
  type HealthWindow,
  type IntegrationEndpointView,
} from '@phoenix/api-client';
import { useIdempotencyKey, usePlatformSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  ListPage,
  SampleBadge,
  SchemaFields,
  StatusChip,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { toNumber } from '../../api';
import { InsurerErrorMapSection } from './InsurerErrorMapSection';

export const SCR_CONSOLE_020 = 'SCR-CONSOLE-020';

export const intConsoleKeys = {
  adapters: ['integration', 'console', 'adapters'] as const,
  health: ['integration', 'console', 'health'] as const,
  endpoints: ['integration', 'console', 'endpoints'] as const,
  secrets: (id: string) => ['integration', 'console', 'secrets', id] as const,
  compliance: ['integration', 'console', 'compliance'] as const,
};

function AdapterTitle({ code, isSample }: { code: string; isSample: boolean }) {
  const { t } = useTranslation('int');
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <span>{t(`int:adapter.${code}`, { defaultValue: code })}</span>
      {isSample ? <SampleBadge /> : null}
    </Stack>
  );
}

function WindowText({ value }: { value: HealthWindow }) {
  const { t } = useTranslation('int');
  const calls = toNumber(value.calls) ?? 0;
  if (calls === 0) {
    return <span>{t('int:health.no_calls')}</span>;
  }
  return (
    <span>
      {t('int:health.window', {
        rate: faDigits(Math.round((toNumber(value.successRate) ?? 0) * 100)),
        calls: faDigits(calls),
        avg: faDigits(toNumber(value.avgMs) ?? 0),
        p95: faDigits(toNumber(value.p95Ms) ?? 0),
      })}
    </span>
  );
}

export function IntegrationsPage() {
  const { t } = useTranslation('int');
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [secretsFor, setSecretsFor] = useState<IntegrationEndpointView | null>(null);
  const [testResult, setTestResult] = useState<ConnectionTestView | null>(null);

  const adapters = useQuery({
    queryKey: intConsoleKeys.adapters,
    queryFn: () => unwrap(api.GET('/api/v1/integration/adapters')),
  });
  const health = useQuery({
    queryKey: intConsoleKeys.health,
    queryFn: () => unwrap(api.GET('/api/v1/integration/platform/health')),
    refetchInterval: 60_000,
  });
  const endpoints = useQuery({
    queryKey: intConsoleKeys.endpoints,
    queryFn: () => unwrap(api.GET('/api/v1/integration/platform/endpoints')),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: intConsoleKeys.endpoints });

  const test = useMutation({
    mutationFn: (endpoint: IntegrationEndpointView) =>
      unwrap(api.POST('/api/v1/integration/platform/endpoints/{id}:test', { params: { path: { id: endpoint.id } } })),
    onSuccess: (result) => {
      setTestResult(result);
      refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: (endpoint: IntegrationEndpointView) =>
      unwrap(
        api.PUT('/api/v1/integration/platform/endpoints/{id}', {
          params: { path: { id: endpoint.id } },
          body: {
            settings: endpoint.settings,
            isActive: !endpoint.isActive,
            priority: endpoint.priority,
            rowVersion: endpoint.rowVersion,
          },
        }),
      ),
    onSuccess: refresh,
  });

  const adapterColumns: DataTableColumn<AdapterView>[] = [
    { id: 'code', headerKey: 'int:console.adapter_columns.code', cell: (row) => <AdapterTitle code={row.code} isSample={row.isSample} /> },
    { id: 'port', headerKey: 'int:console.adapter_columns.port', cell: (row) => <bdi dir="ltr">{row.port}</bdi> },
    { id: 'kind', headerKey: 'int:console.adapter_columns.kind', cell: (row) => t(`int:kind.${row.kind}`, { defaultValue: row.kind }) },
    {
      id: 'level',
      headerKey: 'int:console.adapter_columns.level',
      cell: (row) => t(`int:level.${row.credentialLevel}`, { defaultValue: row.credentialLevel }),
    },
    {
      id: 'status',
      headerKey: 'int:console.adapter_columns.status',
      cell: (row) => <bdi dir="ltr" data-testid={`adapter-status-${row.code}`}>{row.status}</bdi>,
    },
  ];

  const healthColumns: DataTableColumn<AdapterHealthView>[] = [
    {
      id: 'adapter',
      headerKey: 'int:console.aggregate_columns.adapter',
      cell: (row) => <AdapterTitle code={row.adapterCode} isSample={row.isSample} />,
    },
    {
      id: 'endpoints',
      headerKey: 'int:console.aggregate_columns.endpoints',
      cell: (row) => `${faDigits(toNumber(row.activeEndpoints) ?? 0)} / ${faDigits(toNumber(row.endpoints) ?? 0)}`,
    },
    {
      id: 'circuits',
      headerKey: 'int:console.aggregate_columns.circuits',
      cell: (row) => <span data-testid={`aggregate-circuits-${row.adapterCode}`}>{faDigits(toNumber(row.openCircuits) ?? 0)}</span>,
    },
    { id: 'tenants', headerKey: 'int:console.aggregate_columns.tenants', cell: (row) => faDigits(toNumber(row.tenants) ?? 0) },
    { id: 'failing', headerKey: 'int:console.aggregate_columns.failing', cell: (row) => faDigits(toNumber(row.failingTenants) ?? 0) },
    { id: 'hour', headerKey: 'int:console.aggregate_columns.hour', cell: (row) => <WindowText value={row.lastHour} /> },
    { id: 'day', headerKey: 'int:console.aggregate_columns.day', cell: (row) => <WindowText value={row.lastDay} /> },
    { id: 'dead_letters', headerKey: 'int:console.aggregate_columns.dead_letters', cell: (row) => faDigits(toNumber(row.openDeadLetters) ?? 0) },
  ];

  const endpointColumns: DataTableColumn<IntegrationEndpointView>[] = [
    {
      id: 'adapter',
      headerKey: 'int:connections.columns.adapter',
      cell: (row) => <AdapterTitle code={row.adapterCode} isSample={row.isSample} />,
    },
    { id: 'port', headerKey: 'int:connections.columns.port', cell: (row) => <bdi dir="ltr">{row.port}</bdi> },
    {
      id: 'status',
      headerKey: 'int:connections.columns.status',
      cell: (row) => (
        <span data-testid={`platform-endpoint-status-${row.id}`} data-active={String(row.isActive)}>
          <StatusChip tone={row.isActive ? 'active' : 'inactive'} />
        </span>
      ),
    },
    {
      id: 'last_test',
      headerKey: 'int:connections.columns.last_test',
      cell: (row) =>
        row.lastTestOk === null || row.lastTestOk === undefined
          ? t('int:connections.not_tested')
          : row.lastTestOk
            ? t('int:connections.test_ok')
            : t('int:connections.test_failed', { code: row.lastTestCode ?? '—' }),
    },
    {
      id: 'actions',
      headerKey: 'int:connections.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('int:connections.test')}>
            <span>
              <IconButton
                aria-label={`${t('int:connections.test')} ${row.adapterCode}`}
                onClick={() => test.mutate(row)}
                disabled={test.isPending}
                data-testid={`platform-endpoint-test-${row.id}`}
              >
                <NetworkCheckOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={row.isActive ? t('int:connections.inactive') : t('int:connections.active')}>
            <span>
              <IconButton
                aria-label={`${row.isActive ? t('int:connections.inactive') : t('int:connections.active')} ${row.adapterCode}`}
                onClick={() => toggle.mutate(row)}
                disabled={toggle.isPending}
                data-testid={`platform-endpoint-toggle-${row.id}`}
              >
                <PowerSettingsNewOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('int:connections.secrets_title')}>
            <IconButton
              aria-label={`${t('int:connections.secrets_title')} ${row.adapterCode}`}
              onClick={() => setSecretsFor(row)}
              data-testid={`platform-endpoint-secrets-${row.id}`}
            >
              <KeyOutlinedIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const egress = health.data?.egressIps ?? [];
  const adapterOf = (code: string) => adapters.data?.items.find((item) => item.code === code);

  return (
    <ListPage screenId={SCR_CONSOLE_020} titleKey="int:console.title" subtitleKey="int:console.subtitle">
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Typography variant="h6" component="h2">
            {t('int:console.adapters_title')}
          </Typography>
          <DataTable
            captionKey="int:console.adapters_caption"
            columns={adapterColumns}
            rows={adapters.data?.items}
            getRowId={(row) => row.code}
            isLoading={adapters.isPending}
            error={adapters.error}
            onRetry={() => void adapters.refetch()}
          />
        </Stack>
        <Stack spacing={1}>
          <Typography variant="h6" component="h2">
            {t('int:console.aggregate_title')}
          </Typography>
          {health.data && health.data.alerts.length > 0 ? (
            <Stack spacing={1} data-testid="console-alerts">
              {health.data.alerts.map((alert) => (
                <Alert key={alert.id} severity="warning">
                  {t(`int:health.alert.${alert.kind}`, {
                    adapter: alert.adapterCode ? t(`int:adapter.${alert.adapterCode}`, { defaultValue: alert.adapterCode }) : '—',
                    value: faDigits(toNumber(alert.value) ?? 0),
                    defaultValue: alert.kind,
                  })}
                </Alert>
              ))}
            </Stack>
          ) : null}
          <DataTable
            captionKey="int:console.aggregate_caption"
            columns={healthColumns}
            rows={health.data?.adapters}
            getRowId={(row) => row.adapterCode}
            isLoading={health.isPending}
            error={health.error}
            onRetry={() => void health.refetch()}
            emptyTitleKey="int:health.no_calls"
          />
        </Stack>
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="h2">
              {t('int:console.platform_title')}
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setCreating(true)}
              disabled={!adapters.data}
              data-testid="platform-endpoint-create"
            >
              {t('int:connections.add')}
            </Button>
          </Stack>
          {test.error ? <ErrorState error={test.error} compact /> : null}
          {toggle.error ? <ErrorState error={toggle.error} compact /> : null}
          {testResult ? (
            <Alert
              severity={testResult.ok ? 'success' : 'error'}
              onClose={() => setTestResult(null)}
              data-testid="connection-test-result"
              data-ok={String(testResult.ok)}
            >
              {testResult.ok
                ? t('int:connections.test_result_ok')
                : t('int:connections.test_result_failed', {
                    code: testResult.error?.code ?? testResult.resultCode,
                    reason: testResult.error?.reason ?? testResult.resultCode,
                  })}
            </Alert>
          ) : null}
          <DataTable
            captionKey="int:console.platform_caption"
            columns={endpointColumns}
            rows={endpoints.data?.items}
            getRowId={(row) => row.id}
            isLoading={endpoints.isPending}
            error={endpoints.error}
            onRetry={() => void endpoints.refetch()}
            emptyTitleKey="int:connections.empty"
          />
        </Stack>
        <Stack spacing={1} data-testid="egress-ips">
          <Typography variant="h6" component="h2">
            {t('int:console.egress_title')}
          </Typography>
          {egress.length === 0 ? (
            <Typography color="text.secondary">{t('int:console.egress_none')}</Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('int:console.egress_hint')}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {egress.map((ip) => (
                  <Chip key={ip} label={<bdi dir="ltr">{ip}</bdi>} variant="outlined" />
                ))}
              </Stack>
            </>
          )}
        </Stack>

        {/* AP-INT-09 (US-INT-108): کاتالوگ نسخه‌دار نگاشت خطای بیمه‌گر و شمارنده خطاهای نگاشت‌نشده. */}
        <InsurerErrorMapSection />
      </Stack>
      {creating && adapters.data ? (
        <PlatformEndpointDialog adapters={adapters.data.items} onClose={() => setCreating(false)} />
      ) : null}
      {secretsFor ? (
        <PlatformSecretsDialog
          endpoint={secretsFor}
          secretNames={adapterOf(secretsFor.adapterCode)?.secretNames ?? []}
          onClose={() => setSecretsFor(null)}
        />
      ) : null}
    </ListPage>
  );
}

function PlatformEndpointDialog({ adapters, onClose }: { adapters: readonly AdapterView[]; onClose: () => void }) {
  const { t } = useTranslation('int');
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const candidates = adapters.filter((adapter) => adapter.isRegistered && adapter.port !== 'IPaymentGateway');
  const [adapterCode, setAdapterCode] = useState(candidates[0]?.code ?? '');
  const [settings, setSettings] = useState<Record<string, unknown>>({});
  const adapter = candidates.find((item) => item.code === adapterCode);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/v1/integration/platform/endpoints', {
          body: { adapterCode, branchId: null, providerId: null, settings, isActive: false, priority: 100 },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: intConsoleKeys.endpoints });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      titleKey="int:connections.create_title"
      screenId={SCR_CONSOLE_020}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      isDirty={Object.keys(settings).length > 0}
      isSubmitting={save.isPending}
      submitError={save.error}
      maxWidth="md"
    >
      <TextField
        select
        label={t('int:connections.choose_adapter')}
        value={adapterCode}
        onChange={(event) => {
          setAdapterCode(event.target.value);
          setSettings({});
        }}
        slotProps={{ htmlInput: { 'data-testid': 'platform-endpoint-adapter' } }}
      >
        {candidates.map((item) => (
          <MenuItem key={item.code} value={item.code}>
            {t(`int:adapter.${item.code}`, { defaultValue: item.code })}
          </MenuItem>
        ))}
      </TextField>
      {adapter?.isSample ? <SampleBadge /> : null}
      <SchemaFields
        schema={adapter?.settingsSchema}
        value={settings}
        onChange={setSettings}
        labelFor={(key) => t(`int:settings.${key}`, { defaultValue: key })}
        idPrefix="platform-endpoint-setting"
      />
      {adapter?.kind === 'Real' ? <Alert severity="info">{t('int:connections.requires_test_hint')}</Alert> : null}
    </FormDialog>
  );
}

function PlatformSecretsDialog({
  endpoint,
  secretNames,
  onClose,
}: {
  endpoint: IntegrationEndpointView;
  secretNames: readonly string[];
  onClose: () => void;
}) {
  const { t } = useTranslation('int');
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const titleId = useId();
  const [values, setValues] = useState<Record<string, string>>({});
  const secrets = useQuery({
    queryKey: intConsoleKeys.secrets(endpoint.id),
    queryFn: () =>
      unwrap(api.GET('/api/v1/integration/platform/endpoints/{id}/secrets', { params: { path: { id: endpoint.id } } })),
  });
  const save = useMutation({
    mutationFn: (name: string) =>
      unwrap(
        api.PUT('/api/v1/integration/platform/endpoints/{id}/secrets/{name}', {
          params: { path: { id: endpoint.id, name } },
          body: { value: values[name] ?? '', expiresAt: null },
        }),
      ),
    onSuccess: (_view, name) => {
      setValues((current) => ({ ...current, [name]: '' }));
      void queryClient.invalidateQueries({ queryKey: intConsoleKeys.secrets(endpoint.id) });
      void queryClient.invalidateQueries({ queryKey: intConsoleKeys.endpoints });
    },
  });
  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>
        {t('int:connections.secrets_title')} — {t(`int:adapter.${endpoint.adapterCode}`, { defaultValue: endpoint.adapterCode })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">{t('int:connections.secret_hint')}</Alert>
          {secretNames.length === 0 ? <Typography>{t('int:connections.no_secrets')}</Typography> : null}
          {secretNames.map((name) => {
            const current = secrets.data?.items.find((item) => item.name === name);
            const label = t(`int:secret.${name}`, { defaultValue: name });
            return (
              <Stack key={name} spacing={1}>
                <Typography variant="body2" data-testid={`platform-secret-state-${name}`}>
                  {label}:{' '}
                  {current
                    ? current.last4
                      ? t('int:connections.secret_saved', { last4: current.last4 })
                      : t('int:connections.secret_saved_short')
                    : t('int:connections.secret_missing')}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <TextField
                    type="password"
                    autoComplete="new-password"
                    size="small"
                    fullWidth
                    label={`${label} — ${t('int:connections.secret_value')}`}
                    value={values[name] ?? ''}
                    onChange={(event) => setValues((v) => ({ ...v, [name]: event.target.value }))}
                    slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': `platform-secret-value-${name}` } }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => save.mutate(name)}
                    disabled={!values[name] || save.isPending}
                    data-testid={`platform-secret-save-${name}`}
                  >
                    {t('int:connections.secret_save')}
                  </Button>
                </Stack>
              </Stack>
            );
          })}
          {save.error ? <ErrorState error={save.error} compact /> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
